import { type ChangeEvent, type FC, type KeyboardEvent, useEffect, useRef, useState } from 'react';

import { Search } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Rnd } from 'react-rnd';
import { useLocation } from 'react-router-dom';

import type { FilterFormValues } from '@shared/api/findings/filter-form.schema';
import { useGetFacetedFiltersQuery } from '@shared/api/findings/findings.api';
import type { FilterFieldValue, SetFilterFormValue } from '@shared/api/findings/findings.types';
import { InputGroup, InputGroupAddon, InputGroupInput, Spinner } from '@shared/components/ui';
import { DismissButton } from '@shared/components/ui/custom/buttons/DismissButton';
import { useRndModal } from '@shared/hooks/findings/useDraggableResizableModal';
import { useEscapeToClose } from '@shared/hooks/findings/useEscapeToClose';
import { useFiltersFromSearchParams } from '@shared/hooks/findings/useFiltersFromSearchParams';
import { useClickOutside } from '@shared/hooks/useClickOutside';
import { cn } from '@shared/utils';
import { getModalPosition, mapFacetedFiltersToUI } from '@shared/utils/findings/filtering.utils';
import { normalizeOperator } from '@shared/utils/findings/trace-findings.helpers';
import {
  allFieldsHaveValue,
  cleanFieldValuesForSubmission,
  filtersHaveChanged,
  isApplyDisabled,
  normalizeFiltersForUI,
  resetFilters,
  toggleFieldHelper,
} from '@shared/utils/findings/trace-findings.utils';

import { MODAL_DEFAULTS } from './constants';
import { FilterConditionRows } from './FilterConditionRows';
import { FilteringModalErrorState } from './FilteringModalErrorState';
import { FilteringModalFooter } from './FilteringModalFooter';
import { FilteringModalHeader } from './FilteringModalHeader';
import { FiltersListSection } from './FiltersListSection';

interface FilteringModalProps {
  onClose: () => void;
  investigationId: string;
  triggerTop?: number;
  triggerLeft?: number;
  triggerWidth?: number;
  state?: 'open' | 'closed';
}

export const FilteringModal: FC<FilteringModalProps> = ({
  onClose,
  investigationId,
  triggerTop,
  triggerLeft,
  triggerWidth,
  state = 'open',
}) => {
  const location = useLocation();
  const lastPathRef = useRef<string | undefined>(undefined);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const modalWrapperRef = useRef<HTMLDivElement>(null);

  const [closeAfterApply, setCloseAfterApply] = useState(true);
  const [filterSearch, setFilterSearch] = useState('');

  const { left: computedLeft, top: computedTop } = getModalPosition(triggerTop, triggerLeft, triggerWidth);
  const { initialWidth } = MODAL_DEFAULTS;
  const { dragProps, width } = useRndModal({
    initialLeft: computedLeft,
    initialTop: computedTop,
    initialWidth,
    dragAxis: 'x',
    resizeAxis: 'x',
  });

  const { filters: filtersQuery, setFilters } = useFiltersFromSearchParams();

  const [localFilters, setLocalFilters] = useState<Record<string, FilterFieldValue>>(() => ({ ...filtersQuery }));

  const {
    data: filtersDataApi,
    isLoading: isFiltersLoading,
    isFetching: isFiltersFetching,
    isError,
    refetch,
  } = useGetFacetedFiltersQuery({ investigationId, filtersQuery: localFilters }, { skip: !investigationId });

  const filtersData = mapFacetedFiltersToUI(filtersDataApi);

  // RTK Query retains the previous `data` across argument changes, so once the facets have
  // loaded once we keep the modal interactive on subsequent refetches and only surface a
  // subtle progress bar. The blocking overlay is reserved for the very first load.
  const hasLoadedFiltersOnce = filtersDataApi !== undefined;
  const showInitialOverlay = (isFiltersLoading || isFiltersFetching) && !hasLoadedFiltersOnce;
  const showBackgroundProgress = isFiltersFetching && hasLoadedFiltersOnce;

  const { control, handleSubmit, setValue, watch, reset, getValues, formState, getFieldState } =
    useForm<FilterFormValues>({
      defaultValues: {
        selectedFields: [],
        fieldValues: {},
      },
    });

  useEffect(() => {
    if (lastPathRef.current !== undefined && lastPathRef.current !== location.pathname) {
      onClose();
    }
    lastPathRef.current = location.pathname;
  }, [location.pathname, onClose]);

  useEffect(() => {
    const selectedFields = Object.keys(filtersQuery);
    const fieldValues: Record<string, FilterFieldValue> = {};
    for (const key of selectedFields) {
      const fv = filtersQuery[key];
      if (fv && Array.isArray(fv.values)) {
        fieldValues[key] = {
          operator: normalizeOperator(fv.operator),
          values: [...fv.values],
        };
      }
    }
    reset({ selectedFields, fieldValues });
    setLocalFilters({ ...filtersQuery });
  }, [filtersQuery, reset]);

  useEscapeToClose(onClose, true);
  useClickOutside(modalWrapperRef, () => {
    onClose();
  });
  const selectedFields = watch('selectedFields');
  const fieldValues = watch('fieldValues');

  const allFieldsHaveValueResult = allFieldsHaveValue(selectedFields, fieldValues);
  const hasChanges = filtersHaveChanged(localFilters, filtersQuery);

  const toggleField = (field: string) => {
    toggleFieldHelper({
      field,
      getValues,
      setValue: setValue as SetFilterFormValue,
      filtersData: normalizeFiltersForUI(filtersData),
      filterSearch,
      updateFiltersFromForm,
    });
  };

  function updateFiltersFromForm() {
    const data = getValues();
    const cleanedFieldValues = cleanFieldValuesForSubmission(data);
    setLocalFilters(cleanedFieldValues);
  }

  function handleDropdownClose(fieldKey: string) {
    const state = getFieldState(`fieldValues.${fieldKey}.values`, formState);
    if (state.isDirty || state.isTouched) {
      updateFiltersFromForm();
    }
  }

  function handleFormSubmit() {
    setFilters(localFilters);
    if (closeAfterApply) onClose();
  }

  const handleReset = () => {
    resetFilters({ setFilters, reset });
    setLocalFilters({});
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Escape') return;

    if (filterSearch.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      setFilterSearch('');
    }
  };

  return (
    <Rnd
      {...dragProps}
      default={{ x: computedLeft, y: computedTop, width, height: 'auto' }}
      className="fixed min-w-lg z-3"
      dragHandleClassName="filtering-modal-header"
    >
      <div
        ref={modalWrapperRef}
        data-state={state}
        className={cn(
          'bg-popover text-white rounded-xl shadow-2xl border border-muted-border origin-top-left relative',
          'data-[state=open]:animate-in data-[state=open]:zoom-in-90 data-[state=open]:fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:zoom-out-90 data-[state=closed]:fade-out-0',
        )}
        data-testid="trace-findings-filtering-modal"
      >
        {showInitialOverlay && (
          <div
            className="absolute inset-0 bg-[rgba(20,20,20,0.55)] z-2000 flex items-center justify-center pointer-events-auto animate-slide-down rounded-xl"
            aria-label="Loading filters"
            data-testid="trace-findings-filtering-modal-loading"
          >
            <Spinner className="w-14 h-14 text-primary" />
          </div>
        )}
        {showBackgroundProgress && (
          <div
            className="absolute top-0 left-0 right-0 h-1 overflow-hidden rounded-t-xl z-2000 pointer-events-none"
            aria-label="Updating filter counts"
            data-testid="trace-findings-filtering-modal-progress"
          >
            <div className="h-full w-1/3 rounded-full bg-primary animate-progress" />
          </div>
        )}
        <FilteringModalHeader onClose={onClose} isError={isError} data-testid="trace-findings-filtering-modal-header" />
        {isError ? (
          <FilteringModalErrorState refetch={refetch} />
        ) : (
          <>
            <div className="p-4" data-testid="trace-findings-filtering-modal-content">
              <div className="mb-4" data-testid="trace-findings-filtering-modal-search">
                <InputGroup>
                  <InputGroupAddon align="inline-start">
                    <Search className="text-muted-foreground" />
                  </InputGroupAddon>
                  <InputGroupInput
                    ref={searchInputRef}
                    type="text"
                    value={filterSearch}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilterSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search fields..."
                    autoFocus
                    className="focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                    data-testid="trace-findings-filtering-modal-search-input"
                    id="trace-findings-filtering-modal-search-input"
                  />
                  {filterSearch.length > 0 && (
                    <InputGroupAddon align="inline-end">
                      <DismissButton
                        aria-label="Clear search"
                        onClick={() => {
                          setFilterSearch('');
                          searchInputRef.current?.focus();
                        }}
                        className="shrink-0 opacity-50 hover:opacity-100"
                        data-testid="trace-findings-filtering-modal-search-clear"
                      />
                    </InputGroupAddon>
                  )}
                </InputGroup>
              </div>
              <FiltersListSection
                filtersData={filtersData}
                filterSearch={filterSearch}
                selectedFields={selectedFields}
                toggleField={toggleField}
                className="min-h-32"
                data-testid="trace-findings-filtering-modal-filters-list"
              />
              <div className="mt-4">
                <FilterConditionRows
                  selectedFields={selectedFields}
                  filtersData={filtersData}
                  filterSearch={filterSearch}
                  control={control}
                  handleDropdownClose={handleDropdownClose}
                  updateFiltersFromForm={updateFiltersFromForm}
                  toggleField={toggleField}
                />
              </div>
            </div>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <FilteringModalFooter
                onReset={handleReset}
                applyDisabled={isApplyDisabled({
                  isFetching: isFiltersFetching,
                  hasChanges,
                  selectedFields,
                  allFieldsHaveValue: allFieldsHaveValueResult,
                })}
                resetDisabled={Object.keys(localFilters).length === 0}
                onCloseAfterApplyChange={setCloseAfterApply}
              />
            </form>
          </>
        )}
      </div>
    </Rnd>
  );
};
